import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Stack, Typography } from '@mui/material';

// Liten utility-row som visar label + värde i sammanfattningskolumnen.
type SummaryRowProps = { label: string; value: string };
const SummaryRow = (props: SummaryRowProps) => {
  const { label, value } = props;
  return (
    <Box>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
};

type SendMessageSummaryProps = {
  // Override för mottagar-etiketten — användbart i editPage där selection ligger i parent-state, inte i formuläret.
  recipientsLabel?: string;
  // Antal befintliga bilagor (från servern) som redan är kopplade till utskicket.
  // I editPage ligger dessa i parent-state (documentFiles), inte i formuläret —
  // de måste räknas separat utöver formulärets nya `files`.
  existingAttachmentsCount?: number;
};

// Sammanfattning av utskickets formulärvärden. Återanvänds både i SendMessageModal och på edit-sidan
// för redan skickade utskick där formuläret inte längre är meningsfullt att visa.
export const SendMessageSummary = (props: SendMessageSummaryProps) => {
  const { recipientsLabel: recipientsLabelOverride, existingAttachmentsCount = 0 } = props;
  const { t } = useTranslation(['groupMessage']);
  const { watch } = useFormContext();

  const recipients = watch('recipients') as string | undefined;
  const subject = watch('subject') as string | undefined;
  const sender = watch('sender') as string | undefined;
  const senderTitle = watch('senderTitle') as string | undefined;
  const speedFormText = watch('speedFormText') as string | undefined;
  const selectionName = watch('selectionName') as string | undefined;
  const files = (watch('files') as File[] | undefined) ?? [];
  const totalAttachments = files.length + existingAttachmentsCount;

  const recipientsLabel = (() => {
    if (recipientsLabelOverride) return recipientsLabelOverride;
    if (speedFormText) return speedFormText;
    if (selectionName) return selectionName;
    return t('groupMessage:create.summary.noSelection');
  })();

  return (
    <Stack spacing={1.5} divider={<Divider flexItem />}>
      <SummaryRow label={t('groupMessage:create.summary.recipients') as string} value={recipientsLabel as string} />
      <SummaryRow label={t('groupMessage:create.summary.cc') as string} value={recipients || '-'} />
      <SummaryRow label={t('groupMessage:create.summary.subject') as string} value={subject || '-'} />
      <SummaryRow label={t('groupMessage:create.summary.senderTitle') as string} value={senderTitle || '-'} />
      <SummaryRow label={t('groupMessage:create.summary.sender') as string} value={sender || '-'} />
      <SummaryRow label={t('groupMessage:create.summary.attachments') as string} value={String(totalAttachments)} />
    </Stack>
  );
};
